import { useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Link, Navigate, useParams } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { IMAGES } from "@/data/content";
import { ProjectMediaGallery } from "@/components/landing/ProjectMediaGallery";
import { ProjectNarrative } from "@/components/landing/ProjectNarrative";

const detailKeys = ["focus", "approach", "result"];

export default function ProjectDetail() {
  const { projectId } = useParams();
  const { t } = useLang();
  const projectIndex = IMAGES.portfolio.findIndex((item) => item.id === projectId);
  const project = IMAGES.portfolio[projectIndex];
  const detail = t.projects[projectId];

  if (!project || !detail) return <Navigate to="/" replace />;

  const previousProject =
    IMAGES.portfolio[(projectIndex - 1 + IMAGES.portfolio.length) % IMAGES.portfolio.length];
  const nextProject = IMAGES.portfolio[(projectIndex + 1) % IMAGES.portfolio.length];

  return (
    <main data-testid={`project-detail-${project.id}`} className="bg-[#050507] pt-20">
      <section className="relative overflow-hidden border-b border-white/10 py-16 md:py-24 lg:py-32">
        <div className="absolute right-0 top-0 h-px w-2/5 bg-gradient-to-l from-[#00F0FF]/50 to-transparent" />
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <Link
            to="/#portfolio"
            data-testid="project-back-to-portfolio-link"
            className="group inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.18em] text-zinc-400 transition-colors duration-300 hover:text-[#00F0FF]"
          >
            <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1" aria-hidden="true" />
            {t.projectPage.back}
          </Link>

          <div className="mt-16 grid gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:items-end lg:gap-20">
            <div>
              <p className="font-mono text-xs font-bold uppercase tracking-[0.2em] text-[#00F0FF]">
                {project.tag}
              </p>
              <p className="mt-5 text-sm text-zinc-500">{t.projectPage.caseLabel}</p>
            </div>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            >
              <h1 className="font-heading text-4xl font-medium tracking-tight text-white sm:text-5xl lg:text-6xl">
                {project.client}
              </h1>
              <p className="mt-6 max-w-2xl text-base leading-relaxed text-zinc-400">
                {detail.intro}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-10 md:py-14">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <motion.figure
            initial={{ opacity: 0, scale: 0.985 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
            data-testid="project-detail-image"
            className="relative aspect-[16/10] overflow-hidden border border-white/10 bg-[#0A0A0E]"
          >
            <img src={project.src} alt={project.client} className="h-full w-full object-contain" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-transparent to-transparent" />
            <figcaption className="absolute bottom-0 left-0 p-5 font-mono text-[10px] uppercase tracking-[0.18em] text-white/70">
              InovoDesign / {project.tag}
            </figcaption>
          </motion.figure>
        </div>
      </section>

      <ProjectMediaGallery project={project} label={t.projectPage.gallery} />

      <section className="py-16 md:py-24 lg:py-28">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="border-t border-white/10">
            {detailKeys.map((key, index) => (
              <motion.div
                key={key}
                data-testid={`project-detail-${key}`}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.55, delay: index * 0.08, ease: [0.22, 1, 0.36, 1] }}
                className="grid gap-5 border-b border-white/10 py-8 md:grid-cols-[160px_1fr] md:gap-12"
              >
                <p className="font-mono text-xs uppercase tracking-[0.18em] text-[#00F0FF]">
                  {t.projectPage[key]}
                </p>
                <p className="max-w-3xl text-base leading-relaxed text-zinc-300">{detail[key]}</p>
              </motion.div>
            ))}
          </div>

          <div className="mt-16 flex flex-col gap-6 border-l border-[#00F0FF] pl-6 md:mt-20 md:flex-row md:items-center md:justify-between md:pl-8">
            <p className="max-w-xl font-heading text-xl font-medium leading-relaxed text-white sm:text-2xl">
              {t.projectPage.closing}
            </p>
            <Link
              to="/#contact"
              data-testid="project-contact-link"
              className="group inline-flex shrink-0 items-center gap-2 text-sm font-medium text-white transition-colors duration-300 hover:text-[#00F0FF]"
            >
              {t.projectPage.contactCta}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>

      <ProjectNarrative chapters={detail.chapters} label={t.projectPage.story} />

      <nav
        data-testid="project-sequence-navigation"
        aria-label={t.projectPage.caseLabel}
        className="border-t border-white/10"
      >
        <div className="mx-auto grid max-w-[1400px] md:grid-cols-2">
          <Link
            to={`/project/${previousProject.id}`}
            data-testid="project-previous-link"
            className="group border-b border-white/10 px-6 py-10 transition-colors duration-500 hover:bg-white/[0.03] md:border-b-0 md:border-r md:px-12 lg:px-16"
          >
            <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500 transition-colors duration-300 group-hover:text-[#00F0FF]">
              <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1" aria-hidden="true" />
              {t.projectPage.previous}
            </span>
            <span className="mt-4 block font-heading text-2xl font-medium tracking-tight text-white sm:text-3xl">
              {previousProject.client}
            </span>
          </Link>
          <Link
            to={`/project/${nextProject.id}`}
            data-testid="project-next-link"
            className="group px-6 py-10 text-left transition-colors duration-500 hover:bg-white/[0.03] md:px-12 md:text-right lg:px-16"
          >
            <span className="flex items-center justify-start gap-2 font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500 transition-colors duration-300 group-hover:text-[#00F0FF] md:justify-end">
              {t.projectPage.next}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
            </span>
            <span className="mt-4 block font-heading text-2xl font-medium tracking-tight text-white sm:text-3xl">
              {nextProject.client}
            </span>
          </Link>
        </div>
      </nav>
    </main>
  );
}